// @ts-ignore
import type { AccountType, FreightService } from '@prisma/client';

import { compiledRegex, tenants } from './tenants';
import { RequestDrawers, TenantInfo } from './tenants/common';

export { tenants } from './tenants';
export { emailColorMap, type RequestDrawers } from './tenants/common';

type RealTenants = keyof typeof tenants;
export type TenantId = RealTenants | 'any' | 'unknown';

// Canonical shape of the per-tenant customer config. Because each tenant is declared with
// `as const`, `getTenantByHost().customer` is a union of tenant-specific literal shapes and can't be
// read field-by-field; resolve it through this helper to get the full optional config type.
export type TenantCustomerConfig = NonNullable<TenantInfo<string>['customer']>;
export type FreightServicesByAccountType = NonNullable<TenantCustomerConfig['freightServices']>;

export const getCustomerConfig = (hostname: string | null = null): TenantCustomerConfig => {
  const tenant = getTenantByHost(hostname);
  const customer = 'customer' in tenant ? tenant.customer : undefined;
  return (customer ?? {}) as unknown as TenantCustomerConfig;
};

// Built-in per-account-type freight services. Reproduces the historical quote-form behavior and is
// used whenever a tenant does not configure a given account type (or `default`).
const ALL_FREIGHT_SERVICES = ['AFR', 'EXW', 'FCA'] as unknown as FreightService[];
const DEFAULT_FREIGHT_SERVICES: FreightServicesByAccountType = {
  IATA: ['AFR'] as unknown as FreightService[],
  IAC: ['AFR'] as unknown as FreightService[],
  UNCERTIFIED: ['EXW', 'FCA'] as unknown as FreightService[],
  default: ALL_FREIGHT_SERVICES,
};

// Resolves the freight services a customer of the given account type may select on the quote form.
// Tenant config wins per account type, then the tenant's `default`, then the built-in fallback above.
export const getFreightServices = (
  accountType: AccountType | null = null,
  hostname: string | null = null,
): FreightService[] => {
  const configured = getCustomerConfig(hostname).freightServices ?? {};
  const merged = { ...DEFAULT_FREIGHT_SERVICES, ...configured };
  return (accountType ? merged[accountType] : undefined) ?? merged.default ?? ALL_FREIGHT_SERVICES;
};

const domainMap: Record<string, RealTenants> = Object.fromEntries(
  Object.entries(tenants).flatMap(([name, value]: [RealTenants, (typeof tenants)[RealTenants]]) =>
    value.domains.map((d: (typeof tenants)[RealTenants]['domains'][number]) => [d, name] as const),
  ),
);
const domains = Object.keys(domainMap);

export const getTenantByHost = (hostname: string | null = null) => {
  return tenants[getTenantId(hostname)];
};

export const getTenantById = (id: TenantId) => {
  return tenants?.[id as RealTenants];
};

export const getTenantId = (hostname: string | null = null) => {
  // @ts-ignore shared between browser/node
  if (hostname === null) hostname = window?.location?.hostname ?? '';

  const domain = hostname.split(':')[0];

  // Fallback for a special case
  if (domain === 'localhost') return 'pelicargo';
  let bestMatch = '';
  for (let i = 0; i < domains.length; i++) {
    if (!domain.endsWith(domains[i])) continue;
    if (domains[i].length > bestMatch.length) bestMatch = domains[i];
  }
  return domainMap[bestMatch || 'unknown'];
};

export const getAsset = (filename: string) => {
  return `/${getTenantId()}/${filename}`;
};

export const themeEmail = (tenant: TenantId, content: string) => {
  if (tenant === 'unknown' || tenant === 'any') return content;
  return compiledRegex.reduce((agg, func) => func(tenant, agg), content);
};

export const pathIsFlaggable = (path: string) => {
  const tenant = getTenantByHost();
  const basePath = path.split('/')[1];

  const flag = tenant?.admin?.cruds?.[basePath];
  const isDefined = flag !== undefined;
  const isVisible = flag === true ? true : flag === false ? false : undefined;

  return { isDefined, isVisible };
};

export const isCrudRouteEnabledForTenant = (path: string) => {
  const tenant = getTenantByHost();
  const { isDefined, isVisible } = pathIsFlaggable(path);

  // if it's not defined in config, assume it's okay to see for system tenants only
  if (!isDefined) return tenant?.isSystemTenant ?? false;

  return isVisible;
};

export const isRequestDrawerEnabledForTenant = (id: RequestDrawers) => {
  const tenant = getTenantByHost();
  return tenant?.admin?.inbox?.requestDrawers?.[id] ?? false;
};
